import { Alert, Box, Typography, useTheme } from '@mui/material';

import { updateInfoReview } from '@/api/user';

import { Icon } from '@/components/Icon/Icon';
import { FullPageLoaderOverlay } from '@/components/Layout/FullPageLoader';
import { ReviewPersonalDetails } from '@/components/PageForm/PersonalDetails/Review/ReviewPersonalDetails';

import { useMutationWithUserData } from '@/hooks/useMutationWithUserData';
import { useTranslate } from '@/hooks/useTranslate';

import { User } from '@/types/user';

import { getAPIErrorMessage } from '@/utils/api';

import { ReviewPersonalInfoDrawerStyled } from './ReviewPersonalInfoDrawer.styled';

type ReviewPersonalInfoDrawerProps = {
  open: boolean;
  isUserInfoComplete: boolean;
  user: User;
  onClose: () => void;
  onUpdate: () => void;
};

export function ReviewPersonalInfoDrawer({
  open,
  isUserInfoComplete,
  user,
  onClose,
  onUpdate,
}: Readonly<ReviewPersonalInfoDrawerProps>) {
  const { t } = useTranslate();
  const theme = useTheme();

  const { mutate, isPending, error, reset } = useMutationWithUserData({
    mutationFn: updateInfoReview,
    onFinished: () => {
      onClose();
    },
  });

  const onContinue = () => {
    reset();
    mutate({
      reviewed: true,
    });
  };

  const onUpdatePersonalInfo = () => {
    onClose();
    onUpdate();
  };

  if (!user) {
    return null;
  }

  return (
    <ReviewPersonalInfoDrawerStyled
      open={open}
      onClose={onClose}
      hideBackdrop={false}
      title={t('moreInfo.reviewPersonalInfo.drawer.reviewPersonalInfo.title')}
    >
      {isPending && <FullPageLoaderOverlay />}
      {error && (
        <Box my={1.5}>
          <Alert icon={<Icon name="alert" />} color="error" variant="standard">
            {getAPIErrorMessage(error)}
          </Alert>
        </Box>
      )}
      <Typography
        variant="body2"
        textAlign="center"
        mt={2}
        paddingBottom={3}
        color={theme.palette.neutral[800]}
      >
        {t('moreInfo.reviewPersonalInfo.drawer.reviewPersonalInfo.description')}
      </Typography>

      <ReviewPersonalDetails
        onContinue={onContinue}
        onUpdate={onUpdatePersonalInfo}
        user={user}
        isUserInfoComplete={isUserInfoComplete}
      />
    </ReviewPersonalInfoDrawerStyled>
  );
}
